import {
  Box,
  Text,
  Flex,
  Icon,
  toast,
  Image,
  Button,
  RadioInput,
  ModalContext,
  Spinner,
  useScreenResolution,
} from 'dash-ui';
import { useContext } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  GetCurrentBrokerDocument,
  useAttachDefaultStripePaymentMethodMutation,
} from 'gql/generated.graphql';
import { CardContainer } from 'pages/ProfilePage/components/ProfileBilling/ProfileBilling.styles';

import { IPaymentMethodCardItem } from './PaymentMethodCardItem.interfaces';
import { CARD_TO_CARD_LOGO_RELATIONS } from './PaymentMethodCardItem.constants';

function PaymentMethodCardItem({
  paymentMethod,
  defaultPaymentMethodId,
}: IPaymentMethodCardItem) {
  const { paymentMethodId, cardBrand, cardNumber, expiredMonth, expiredYear } =
    paymentMethod;
  const theme = useTheme();
  const imagePath = CARD_TO_CARD_LOGO_RELATIONS[cardBrand];
  const isActive = defaultPaymentMethodId === paymentMethodId;
  const { setPayload, toggleModal } = useContext(ModalContext);
  const { t } = useTranslation(['common', 'pages']);
  const { t: billingPageLabels } = useTranslation('pages', {
    keyPrefix: 'profilePage.billing',
  });
  const {
    devices: { isMobile, isTablet },
  } = useScreenResolution();

  const [setDefaultPaymentMethod, { loading }] =
    useAttachDefaultStripePaymentMethodMutation({
      refetchQueries: [GetCurrentBrokerDocument],
      onCompleted: () => {
        toast.success(t('pages:brokersPage.toast.paymentMethodSetAsDefault'));
      },
      onError: (error) => toast.error(error.toString()),
    });

  return (
    <CardContainer p={4} pr={[3, 0, 0, 0]} mt={4} pos="relative">
      <RadioInput
        mr={3}
        mt={1}
        checked={isActive}
        onChange={() => {
          if (!isActive) {
            setDefaultPaymentMethod({
              variables: { stripePaymentMethodId: paymentMethodId },
            });
          }
        }}
      />

      <Flex flex={1} alignItems="center">
        <CardContainer
          w={48}
          h={48}
          mr={[2, 2, 4, 4]}
          alignItems="center"
          justifyContent="center"
        >
          {imagePath ? (
            <Image src={imagePath} size={40} />
          ) : (
            <Icon size={24} name="bank" color={theme.colors.b100} />
          )}
        </CardContainer>

        <Box>
          <Flex alignItems="center">
            <Flex flex={1} minWidth="50px">
              <Text bold lg={!isMobile && !isTablet}>
                {cardNumber}
              </Text>
            </Flex>
            {isActive && !isMobile && (
              <Box ml={1}>
                <Text color={theme.colors.b100} ellipsis sm={isMobile}>
                  • {billingPageLabels('default')}
                </Text>
              </Box>
            )}
          </Flex>
          <Flex flex={1}>
            <Text color={theme.colors.n70} sm={isMobile || isTablet}>
              {billingPageLabels('expirationDate', {
                expiredMonth,
                expiredYear,
              })}
            </Text>
          </Flex>
        </Box>
      </Flex>

      <Flex flexDirection="column">
        <Button
          mt={[0, 1, 2, 2]}
          mr={2}
          variation="flat"
          colorVariation="danger"
          h={[28, 40, 40, 40]}
          icon="trash"
          iconProps={{ size: 18 }}
          disabled={isActive}
          onClick={() => {
            setPayload(paymentMethodId);
            toggleModal(true, 'RemovePaymentMethodModal');
          }}
        >
          {!isMobile && !isTablet && t('common:labels.delete')}
        </Button>
        {isActive && isMobile && (
          <Box ml={1}>
            <Text color={theme.colors.b100} ellipsis sm={isMobile}>
              {billingPageLabels('default')}
            </Text>
          </Box>
        )}
      </Flex>
      {loading && <Spinner fullContent />}
    </CardContainer>
  );
}

export default PaymentMethodCardItem;
